import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import ItemLocation, { temporaryQuantity } from "./ItemLocation";
import COLORS from "../colors";
import { deepFreeze, formatCurrency, timestampToDate } from "../util/misc";
import { quantityReceived } from "../util/receiving";
import { compact, every, get, isEmpty, last, sortBy } from "lodash";
import { diff } from "deep-object-diff";

import Identicon from "identicon.js";
import sha1 from "sha1";

export const MISC_SERIES = [
  "Accessories",
  "Hardware",
  "Screens",
  "Service Parts",
];

export const SALE_ITEM_FIELDS = {
  line: 0,
  accessoryIndex: 0, // For accessories: 1, 2, 3... (e.g., line 5 with accessoryIndex 2 = "5.2")
  parentID: null, // ID of parent item for accessories
  category: "",
  image: null,
  brand: "",
  series: "",
  description: "",
  labor: null,
  // Cross-sale install: the sale the installing `labor` lives on, when it is a
  // DIFFERENT sale in the project than this item's own (see crossSaleInstall).
  // Null for the normal case where the labor is on this same sale; consumers
  // fall back to this item's saleID. Lets the receiving/readiness flow route a
  // labor update to the right sale without the labor being loaded.
  laborSaleID: null,
  margin: null,
  cost: 0,
  retail: 0,
  price: 0,
  notax: false,
  quantity: 1,
  quantityReady: 0,
  quantityScheduled: 0,
  quantityDelivered: 0,
  quantityInstalled: 0,
  quantityInvoiced: 0,
  fullyScheduled: false,
  fullyDelivered: false,
  fullyInstalled: false,
  fullyInvoiced: false,
  location: "",
  inventoryID: "",
  canceledToInventoryID: null,
  canceledToInventoryIDs: [],
  itemLocations: {},
  atTemporaryDestination: false,
  // Derived from the latest itemLocations by the touchItem Cloud Function, so
  // pages can query the items at one warehouse / `warehouse:zone`.
  warehouses: [],
  warehouseZones: [],

  // Vendor shipping integration
  vendorShipping: null, // { trackingNumbers: [], vendorLineNumber, characterCode, shipmentID }

  // Which panels of a multi-carton unit have been received, keyed by the
  // packing slip they came in on: { [packingSlipID]: ["1", "2"] }.
  //
  // It lives on the ITEM rather than on a slip because a five-panel door's
  // panels can arrive on two trucks against two slips, and per-slip state
  // completes on neither. Keyed by slip inside that so re-receiving one slip
  // replaces its own panels rather than counting them twice -- the same
  // discipline completeReceiving applies to `ready`.
  //
  // Declared here because sanitize() picks only the keys named in this map:
  // an undeclared field survives in Firestore but is dropped the moment any
  // client round-trips the item, which would silently wipe a part-built door.
  receivedPanels: {},

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(SALE_ITEM_FIELDS);

export const SALE_ITEM_STATUS_TYPES = {
  holding: {
    order: 7,
    label: "Holding",
    icon: "holding",
    color: "negative",
  },
  loaded: {
    order: 8,
    label: "Loaded",
    icon: "loaded",
    color: "warning",
  },
  deliveredPartial: {
    order: 9,
    label: "Partially delivered",
    icon: "delivered",
    color: "warning",
    archive: true,
  },
  delivered: {
    order: 10,
    label: "Delivered",
    icon: "delivered",
    color: "positive",
    archive: true,
  },
  installed: {
    order: 11,
    label: "Installed",
    icon: "installation",
    color: "positive",
    archive: true,
  },
};
deepFreeze(SALE_ITEM_STATUS_TYPES);

export default class SaleItem extends DataStructure {
  constructor(projectID, saleID, id, data = {}) {
    data = sanitize(data, SALE_ITEM_FIELDS);

    // Convert to Date
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);

    super(data);

    this.id = id || null;
    this.projectID = projectID || null;
    this.saleID = saleID || null;
    this.permissions = Object.freeze({
      create: SaleItem.canBeCreatedBy,
      edit: this.canBeEditedBy,
      relocate: this.canBeRelocatedBy,
      delete: this.canBeDeletedBy,
    });
  }

  // Permissions

  static canBeCreatedBy(user) {
    return (
      user.isSuper ||
      user.hasRole("quality-control") ||
      user.hasRole("warehouse")
    );
  }

  canBeEditedBy(user) {
    const sale = this.sale;
    return (
      (user.isSuper ||
        user.hasRole("quality-control") ||
        user.hasRole("warehouse")) &&
      sale &&
      !sale.locked
    );
  }

  canBeRelocatedBy(user) {
    const sale = this.sale;
    return user.isEmployee && !this.fullyDelivered && sale && !sale.locked;
  }

  canBeDeletedBy(user) {
    const sale = this.sale;
    return (
      (user.isSuper || (user.hasRole("warehouse") && !this.data.line)) &&
      sale &&
      !sale.locked &&
      MISC_SERIES.includes(this.data.series) &&
      !this.data.quantityScheduled &&
      !this.data.quantityDelivered &&
      !this.data.fullyInvoiced
    );
  }

  // Database

  get path() {
    return SaleItem.getPath(this.projectID, this.saleID, this.id);
  }

  static getPath(projectID, saleID, id) {
    let path = `projects/${projectID}/sales/${saleID}/items`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(saleItem) {
    return saleItem.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new SaleItem(
      snapshot.ref.parent.parent.parent.parent.id,
      snapshot.ref.parent.parent.id,
      snapshot.id,
      data
    );
  }

  static lineAndAccessoryIteratees() {
    return [
      (item) => Number(item?.line || item?.data?.line) || 0,
      (item) => Number(item?.accessoryIndex || item?.data?.accessoryIndex) || 0,
    ];
  }

  static sortByLineAndAccessory(items, iteratees = []) {
    return sortBy(items, [
      ...SaleItem.lineAndAccessoryIteratees(),
      ...(Array.isArray(iteratees) ? iteratees : [iteratees]),
    ]);
  }

  // Editing

  get rules() {
    return {
      brand: [(a) => Boolean(a?.trim()) || "required"],
      series: [(a) => Boolean(a?.trim()) || "required"],
      description: [(a) => Boolean(a?.trim()) || "required"],
      parentID: [
        () => !this.isAccessories || Boolean(this.data.parentID) || "required",
      ],
      quantity: [(a) => a > 0 || "quantityPositive"],
      quantityReady: [
        (a) => !a || a <= this.data.quantity || "readyExceedsTotal",
      ],
      quantityScheduled: [
        (a) => !a || a <= this.data.quantity || "scheduledExceedsTotal",
      ],
      quantityInvoiced: [
        (a) => !a || a <= this.data.quantity || "invoicedExceedsTotal",
      ],
    };
  }

  get isValid() {
    return every(this.rules, (rules, key) =>
      rules.every((rule) => rule(get(this.data, key)) === true)
    );
  }

  itemLocationRules(locations) {
    const total = locations.reduce((sum, l) => sum + (l.quantity || 0), 0);
    return [
      () => total >= this.data.quantityReady && total <= this.data.quantity,
      () => locations.every((l) => !l.quantity || new ItemLocation(l).isValid),
    ];
  }

  get isEmpty() {
    return !this.id && isEmpty(diff(this.data, SALE_ITEM_FIELDS));
  }

  isEqual(saleItem) {
    return this.id === saleItem.id && isEmpty(diff(this.data, saleItem.data));
  }

  // Formatting

  get itemType() {
    return "material";
  }

  get itemLocationItemType() {
    if (this.isAccessories) {
      return "accessory";
    } else if (this.isScreens) {
      return "screen";
    } else if (this.isHardware) {
      return "hardware";
    } else if (this.isServiceParts) {
      return "part";
    } else {
      return "unit";
    }
  }

  locationID(zone, bin) {
    // Encode the bin (when present) so units of this item sitting in different
    // bins of the SAME bin-enabled warehouse zone get distinct loaded-map keys
    // instead of colliding on `${id}-${zone}` and dropping a unit during
    // loading. Bins are 1-999; falsy (null/0) means "no bin".
    return bin ? `${this.id}-${zone}@${bin}` : `${this.id}-${zone}`;
  }

  get isAccessories() {
    return this.data.series === "Accessories";
  }

  get isScreens() {
    return this.data.series === "Screens";
  }

  get isHardware() {
    return this.data.series === "Hardware";
  }

  get isServiceParts() {
    return this.data.series === "Service Parts";
  }

  get icon() {
    return itemIcon(this.data.series);
  }

  get lineNumber() {
    if (!this.data.line) return null;
    if (this.data.accessoryIndex) {
      return `${this.data.line}.${this.data.accessoryIndex}`;
    }
    return String(this.data.line);
  }

  get parent() {
    return this.data.parentID
      ? store.state.main.saleItems[this.data.parentID]
      : null;
  }

  get accessories() {
    return sortBy(
      Object.values(store.state.main.saleItems).filter(
        (item) =>
          item.projectID === this.projectID &&
          item.saleID === this.saleID &&
          item.data.parentID === this.id
      ),
      "data.accessoryIndex"
    );
  }

  get project() {
    return this.projectID ? store.state.main.projects[this.projectID] : null;
  }

  get sale() {
    return this.saleID
      ? store.state.main.sales[this.projectID + this.saleID]
      : null;
  }

  get cost() {
    return this.data.cost === null ? null : formatCurrency(this.data.cost);
  }

  get retail() {
    return this.data.retail === null ? null : formatCurrency(this.data.retail);
  }

  get price() {
    return this.data.price === null ? null : formatCurrency(this.data.price);
  }

  get total() {
    return this.data.price === null
      ? 0
      : formatCurrency(this.data.quantity * this.data.price);
  }

  get totalCost() {
    return this.data.cost === null
      ? null
      : formatCurrency(this.data.quantity * this.data.cost);
  }

  get quantityRemaining() {
    return this.data.quantity - this.data.quantityDelivered;
  }

  // Off the truck, QC'd or not -- `quantityReady` only counts what QC passed.
  get quantityReceived() {
    const sale = this.sale;
    return quantityReceived(this.id, this.data, sale ? sale.data.status : null);
  }

  get issues() {
    return sortBy(
      Object.values(store.state.main.issues).filter(
        (issue) => issue.itemID === this.id
      ),
      "created"
    ).reverse();
  }

  packingSlipIssues(packingSlipID) {
    return this.issues.filter((issue) => issue.packingSlipID === packingSlipID);
  }

  get labor() {
    return this.data.labor
      ? store.state.main.saleLabors[this.data.labor]
      : null;
  }

  // Cross-sale install: when this item is installed by a labor line that lives
  // on a DIFFERENT sale in the project (it was sold on this sale but installed
  // under a later sale), return that installing sale's id for traceability.
  // Null for the normal case where the installing labor is on this same sale,
  // or when the labor isn't loaded. Consumers must null-check.
  get installedBySaleID() {
    const labor = this.labor;
    return labor && labor.saleID && labor.saleID !== this.saleID
      ? labor.saleID
      : null;
  }

  get itemLocation() {
    const keys = Object.keys(this.data.itemLocations).sort();
    if (!keys.length) return [];
    const latest = keys[keys.length - 1];
    return this.data.itemLocations[latest].length
      ? this.data.itemLocations[latest].map((l) => new ItemLocation(l))
      : [];
  }

  // Units on a truck per the latest location snapshot: loaded on a delivery
  // or installation that has not delivered or unloaded them yet.
  get quantityOnVehicle() {
    return this.itemLocation.reduce(
      (total, location) =>
        location.isVehicle
          ? total + (Number(location.data.quantity) || 0)
          : total,
      0
    );
  }

  // Units a delivery can still take on. `quantityScheduled` alone is not
  // enough: removing an item from a loaded delivery, or a partial completion,
  // unschedules units that are still on the truck, and counting them as free
  // let one unit sit on two deliveries (sale 04300138A). Units on a vehicle
  // stay committed until they are delivered or unloaded. `loadedHere` is how
  // many of those units the delivery being edited has loaded itself, so it
  // can take its own units back.
  unscheduledQuantity(loadedHere = 0) {
    const ready = Number(this.data.quantityReady) || 0;
    const scheduled = Number(this.data.quantityScheduled) || 0;
    const delivered = Number(this.data.quantityDelivered) || 0;
    const onVehicle = Math.max(0, this.quantityOnVehicle - (loadedHere || 0));
    return Math.max(0, ready - Math.max(scheduled, delivered + onVehicle));
  }

  get quantityAtTemporaryDestination() {
    return temporaryQuantity(this.data.itemLocations);
  }

  get isAtTemporaryDestination() {
    return this.quantityAtTemporaryDestination > 0;
  }

  get itemLocations() {
    let locations = [];
    Object.keys(this.data.itemLocations)
      .sort()
      .reverse()
      .forEach((key) => {
        locations.push({
          timestamp: new Date(Number(key)),
          locations: this.data.itemLocations[key].map(
            (l) => new ItemLocation(l)
          ),
        });
      });
    return locations;
  }

  get createdBy() {
    return this.data.createdBy && store.state?.main?.users
      ? store.state.main.users[this.data.createdBy]
      : null;
  }

  get modifiedBy() {
    return this.data.modifiedBy && store.state?.main?.users
      ? store.state.main.users[this.data.modifiedBy]
      : null;
  }

  get statusType() {
    if (this.fullyInstalled) {
      return "installed";
    } else if (this.fullyDelivered) {
      return "delivered";
    } else if (this.itemLocation.some((l) => l.isDelivered)) {
      return "deliveredPartial";
    } else if (this.itemLocation.some((l) => l.isVehicle)) {
      return "loaded";
    } else if (this.data.quantityReady > 0) {
      return "holding";
    } else {
      return "";
    }
  }

  get status() {
    const type = SALE_ITEM_STATUS_TYPES[this.statusType];
    return type
      ? { ...type, ...COLORS[type.color], type: this.statusType }
      : null;
  }

  getAvatar(size) {
    return getAvatar(this.data.inventoryID || this.id, size);
  }

  get description() {
    return highlightDescription(this.data.description);
  }

  summary(length = 200) {
    return itemSummary(this.data.description, length);
  }

  toString() {
    return compact([
      this.data.brand,
      this.data.series,
      this.data.location,
      this.summary(),
    ]).join("\n");
  }
}

// An item's icon by its series: the misc series have their own, every other
// item is a unit
export function itemIcon(series) {
  return (
    {
      Accessories: "accessory",
      Screens: "screens",
      Hardware: "hardware",
      "Service Parts": "service",
    }[series] || "window"
  );
}

// An item's description without its leading bold headings, cut to `length`
// characters (all of it for null)
export function itemSummary(description, length = 200) {
  const text = (description || "").replace(/^\*\*+[^*]+\*\*+\s*/gm, "");
  return length === null
    ? text
    : text.slice(0, length) + (text.length > length ? "…" : "");
}

export function getAvatar(id, size = 48) {
  const data = new Identicon(sha1(id), {
    background: [0, 0, 0, 255],
    // foreground: [255, 255, 255, 255],
    size,
  }).toString();
  return `<img width=${size} height=${size} src="data:image/png;base64,${data}" />`;
}

export function highlightDescription(description) {
  return description
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/^\*\*+([^*]+)\*\*+(\s*)/gm, "<em>$1</em>$2")
    .replace(
      /((^|\W)(interior|int|exterior|ext) )?(colou?rs?|finish|species)( options)?/gi,
      "<span class='color'>$&</span>"
    )
    .replace(
      /((^|\W)(interior|exterior) )?grille|grids?|lite divisions/gi,
      "<span class='grid'>$&</span>"
    )
    .replace(/[rl]h|(right|left)( hand)?/gi, "<span class='handing'>$&</span>")
    .replace(/hardware|hinges?/gi, "<span class='hardware'>$&</span>")
    .replace(
      /jambs?|nailing (fins?|flanges?)|casing|trim|brickmould|installation clips|sill nosing/gi,
      "<span class='misc'>$&</span>"
    )
    .replace(
      /(frame |rough opening |unit dimension )?(width|height|size|depth)|\d+w\d+h|\d+ wide \d+ high/gi,
      "<span class='size'>$&</span>"
    )
    .replace(
      /(^|\W)(temp(ered)?( glass)?)(\W|$)/gi,
      "$1<span class='tempered'>$2</span>$5"
    )
    .replace(
      /unit[ :][0-9A-Z]+([ .,]+[0-9A-Z]+)?/gi,
      "<span class='unit'>$&</span>"
    )
    .replace(
      /(^|[\s:(,~]+)(\d+([- ]?[\d/.]+)?['´"m]*( ?x ?\d+([- ]?[\d/.]+)?['´"m]*)*)(?=[\s:),]+|$)/gi,
      "$1<span class='number'>$2</span>"
    );
}
